import { Box, Typography, Paper, IconButton, Avatar, Grow, Button } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight, FormatQuote } from "@mui/icons-material";
import { useState, useEffect, useCallback } from "react";
import _ from "lodash";

function Testimonial({ testimonials, styles, openModal }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [showTestimonial, setShowTestimonial] = useState(true);
    const [isHovering, setIsHovering] = useState(false);
    const MAX_CHARS = 350;

    const handleNext = useCallback(() => {
        if (testimonials && testimonials.length > 0) {
            setShowTestimonial(false);
            setTimeout(() => {
                setActiveIndex((prev) => (prev + 1) % testimonials.length);
                setShowTestimonial(true);
            }, 300);
        }
    }, [testimonials]);

    const handlePrev = () => {
        if (testimonials && testimonials.length > 0) {
            setShowTestimonial(false);
            setTimeout(() => {
                setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
                setShowTestimonial(true);
            }, 300);
        }
    };

    // Auto-advance testimonials
    useEffect(() => {
        if (testimonials && testimonials.length > 1 && !isHovering) {
            const timer = setInterval(() => {
                handleNext();
            }, 8000); // Change testimonial every 8 seconds
            
            return () => clearInterval(timer);
        }
    }, [testimonials, handleNext, isHovering]);

    if (!testimonials || testimonials.length === 0) {
        return (
            <Typography variant="body1" color="text.secondary">
                No testimonials available yet.
            </Typography>
        );
    }

    return (
        <Paper
            elevation={3}
            sx={styles.testimonialPaper}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <Box sx={styles.quoteBackground}>
                <FormatQuote sx={styles.quoteBackgroundIcon} />
            </Box>
            
            <Grow in={showTestimonial} timeout={500}>
                <Box sx={styles.testimonialContent}>
                    <Avatar 
                        src={testimonials[activeIndex].image} 
                        alt={testimonials[activeIndex].name}
                        sx={styles.avatar}
                    />
                    
                    <Box sx={styles.textContainer}>
                        <Typography variant="body1" sx={styles.testimonialText}>
                            "{_.truncate(testimonials[activeIndex].description, {
                                length: MAX_CHARS,
                                separator: ' ',
                                omission: '...',
                            })}"
                            {testimonials[activeIndex].description.length > MAX_CHARS && (
                                <Button 
                                    onClick={() => openModal(activeIndex)} 
                                    color="primary" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                >
                                    Read More
                                </Button>
                            )}
                        </Typography>
                        
                        <Box sx={styles.authorContainer}>
                            <Typography variant="h6" color="primary.main" sx={styles.authorName}>
                                {testimonials[activeIndex].name}
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">
                                {!_.isEmpty(testimonials[activeIndex].position) ?
                                    `${testimonials[activeIndex].position}, ${testimonials[activeIndex].company}`
                                    : testimonials[activeIndex].company}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Grow>
            
            <Box sx={styles.navigationContainer}>
                <IconButton 
                    onClick={handlePrev} 
                    disabled={testimonials.length <= 1}
                    color="primary"
                    sx={styles.navButton}
                >
                    <KeyboardArrowLeft />
                </IconButton>
                
                <Box sx={styles.dotContainer}>
                    {testimonials.map((_, index) => (
                        <Box 
                            key={index}
                            sx={styles.dot(index === activeIndex)}
                            onClick={() => {
                                setShowTestimonial(false);
                                setTimeout(() => {
                                    setActiveIndex(index);
                                    setShowTestimonial(true);
                                }, 300);
                            }}
                        />
                    ))}
                </Box>
                
                <IconButton 
                    onClick={handleNext} 
                    disabled={testimonials.length <= 1}
                    color="primary"
                    sx={styles.navButton}
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Box>
        </Paper>
    );
}

export default Testimonial; 